// Ordernotes.js - UI matched with Users Management page
import React, { useState, useEffect, useCallback } from 'react';
import {
  FaStickyNote, FaSearch, FaPlus, FaTrash, FaEdit, FaCheckDouble,
  FaEllipsisV, FaBuilding, FaMapMarkerAlt
} from 'react-icons/fa';
import { IoMdArrowDropdown } from "react-icons/io";
import { RiDeleteBinFill } from 'react-icons/ri';
import { GiBorderedShield } from 'react-icons/gi';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import './Ordernotes.css';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

const Ordernotes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityFetchError, setCityFetchError] = useState(null);

  const [filterCities, setFilterCities] = useState([]);
  const [selectedFilterCity, setSelectedFilterCity] = useState('');
  const [loadingFilterCities, setLoadingFilterCities] = useState(false);
  const [cityCounts, setCityCounts] = useState({});

  const [formData, setFormData] = useState({
    orderId: '', // This will store the actual order_Id (integer)
    note: '',
    isRequired: false,
    shopId: '',
    city: ''
  });

  const [orderNotes, setOrderNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [error, setError] = useState(null);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [shops, setShops] = useState([]);
  const [shopOrders, setShopOrders] = useState([]);
  const [currentShopName, setCurrentShopName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const getShopIdFromSources = useCallback(() => {
    const urlMatch = location.pathname.match(/\/shop\/(\d+)/);
    if (urlMatch) return urlMatch[1];
    const stored = localStorage.getItem('selectedShopId') || localStorage.getItem('selected_shop_id');
    if (stored) return stored;
    return null;
  }, [location.pathname]);

  const getAuthToken = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token') || localStorage.getItem('accessToken');
    if (token) return token;
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    return userData?.access_token || userData?.token || null;
  };

  const apiFetch = useCallback(async (url, options = {}) => {
    const token = getAuthToken();
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(fullUrl, { ...options, headers });
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }, []);

  // Fetch shops
  useEffect(() => {
    const fetchShops = async () => {
      try {
        const data = await apiFetch('/user/shops/');
        let shopsData = [];
        if (data.results) shopsData = data.results;
        else if (Array.isArray(data)) shopsData = data;
        else if (data.shops) shopsData = data.shops;
        setShops(shopsData);

        const shopIdFromSources = getShopIdFromSources();
        if (shopIdFromSources) {
          const shopIdStr = String(shopIdFromSources);
          const foundShop = shopsData.find(s => String(s.shop_id) === shopIdStr);
          setSelectedShopId(shopIdStr);
          setFormData(prev => ({ ...prev, shopId: shopIdStr }));
          if (foundShop) setCurrentShopName(foundShop.shop_name);
          if (!location.pathname.match(/\/shop\/\d+/)) {
            navigate(`/shop/${shopIdStr}/Ordernotes`, { replace: true });
          }
        } else if (shopsData.length > 0) {
          const firstShop = shopsData[0];
          const firstShopId = String(firstShop.shop_id);
          setSelectedShopId(firstShopId);
          setFormData(prev => ({ ...prev, shopId: firstShopId }));
          setCurrentShopName(firstShop.shop_name);
          localStorage.setItem('selectedShopId', firstShopId);
          navigate(`/shop/${firstShopId}/Ordernotes`, { replace: true });
        }
      } catch (error) {
        console.error('Error fetching shops:', error);
      }
    };
    fetchShops();
  }, [apiFetch, navigate, getShopIdFromSources, location.pathname]);

  // Watch URL for shop changes
  useEffect(() => {
    const shopIdFromSources = getShopIdFromSources();
    if (shopIdFromSources && String(shopIdFromSources) !== String(selectedShopId)) {
      const shopIdStr = String(shopIdFromSources);
      setSelectedShopId(shopIdStr);
      setFormData(prev => ({ ...prev, shopId: shopIdStr }));
      const shop = shops.find(s => String(s.shop_id) === shopIdStr);
      if (shop) setCurrentShopName(shop.shop_name);
    }
  }, [location.pathname, shops, selectedShopId, getShopIdFromSources]);

  // Fetch cities
  useEffect(() => {
    const fetchShopCities = async () => {
      if (!selectedShopId) {
        setCities([]);
        setFilterCities([]);
        setSelectedFilterCity('');
        setFormData(prev => ({ ...prev, city: '' }));
        setCityFetchError(null);
        return;
      }
      try {
        setLoadingCities(true);
        setLoadingFilterCities(true);
        setCityFetchError(null);
        const response = await apiFetch(`/order/shop-cities/?shop_id=${selectedShopId}`);

        let citiesData = [];
        if (response.cities && Array.isArray(response.cities)) citiesData = response.cities;
        else if (response.data && Array.isArray(response.data.cities)) citiesData = response.data.cities;
        else if (Array.isArray(response)) citiesData = response;

        setCities(citiesData);
        setFilterCities(citiesData);
        setSelectedFilterCity('');

        if (formData.city && !citiesData.includes(formData.city)) {
          setFormData(prev => ({ ...prev, city: '' }));
        }
      } catch (err) {
        console.error('Error fetching shop cities:', err);
        setCityFetchError('Failed to load cities for this shop');
        setCities([]);
        setFilterCities([]);
        setSelectedFilterCity('');
      } finally {
        setLoadingCities(false);
        setLoadingFilterCities(false);
      }
    };
    fetchShopCities();
  }, [selectedShopId, apiFetch]);

  const handleFilterCityChange = (e) => {
    setSelectedFilterCity(e.target.value);
  };

  // Fetch orders
  useEffect(() => {
    const fetchShopOrders = async () => {
      if (!selectedShopId) return;
      try {
        const url = `/order/getAllorder/?shop_id=${selectedShopId}`;
        const response = await apiFetch(url);

        let ordersArray = [];
        if (response && response.orders && Array.isArray(response.orders)) {
          ordersArray = response.orders;
        } else if (Array.isArray(response)) {
          ordersArray = response;
        } else if (response && response.results) {
          ordersArray = response.results;
        }

        setShopOrders(ordersArray);
      } catch (error) {
        console.error('Error fetching shop orders:', error);
        setShopOrders([]);
      }
    };
    fetchShopOrders();
  }, [selectedShopId, apiFetch]);

  // Fetch notes with city_counts
  useEffect(() => {
    const fetchOrderNotes = async () => {
      if (!selectedShopId) return;
      try {
        setError(null);
        const notesUrl = `/order/api/order-notes/?shop_id=${selectedShopId}`;
        const response = await apiFetch(notesUrl);

        let notesDataFromApi = [];
        let cityCountsData = {};

        if (response && response.notes) {
          notesDataFromApi = response.notes;
          cityCountsData = response.city_counts || {};
        } else if (Array.isArray(response)) {
          notesDataFromApi = response;
        }

        const notesData = [];

        notesDataFromApi.forEach((note) => {
          const cityValue =
            typeof note.city === 'string'
              ? note.city
              : note.city?.city_name ||
                note.city?.name ||
                note.city?.city ||
                '';

          notesData.push({
            id: note.id,
            orderId: note.order_tracking_id || `ORD-${note.order_id || 'N/A'}`,
            note: note.note || '',
            isRequired: note.is_required || false,
            order_Id: note.order_id,
            shop_id: note.shop?.shop_id || note.shop_id,
            shop_name: note.shop?.shop_name || note.shop_name || 'N/A',
            city: String(cityValue).trim(),
            created_at: note.created_at,
            type: 'order_note_model'
          });
        });

        notesData.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        setOrderNotes(notesData);
        setCityCounts(cityCountsData);
      } catch (err) {
        console.error('Error fetching order notes:', err);
        setError(err.message);
      }
    };
    fetchOrderNotes();
  }, [selectedShopId, apiFetch]);

  // Filter
  useEffect(() => {
    let filtered = orderNotes;

    if (selectedFilterCity && selectedFilterCity.trim() !== '') {
      filtered = filtered.filter(note => note.city === selectedFilterCity);
    }

    if (searchTerm) {
      filtered = filtered.filter(note =>
        (note.orderId && note.orderId.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (note.note && note.note.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (note.city && note.city.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredNotes(filtered);
  }, [orderNotes, searchTerm, selectedFilterCity]);

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const handleSelectAll = () => {
    if (selectedItems.length === filteredNotes.length) setSelectedItems([]);
    else setSelectedItems(filteredNotes.map(note => note.id));
    setShowActionDropdown(false);
  };

  const handleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) setSelectedItems(selectedItems.filter(id => id !== itemId));
    else setSelectedItems([...selectedItems, itemId]);
    setShowActionDropdown(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) {
      await Swal.fire({ icon: 'warning', title: 'No Selection', text: 'Please select items to delete', confirmButtonColor: '#004aad' });
      return;
    }
    const result = await Swal.fire({
      title: 'Delete Selected Notes?',
      text: `Are you sure you want to delete ${selectedItems.length} note(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        for (const noteId of selectedItems) {
          const noteToDelete = orderNotes.find(n => n.id === noteId);
          if (noteToDelete && noteToDelete.type === 'order_note_model' && noteToDelete.id) {
            await apiFetch(`/order/api/order-notes/${noteToDelete.id}/?shop_id=${selectedShopId}`, { method: 'DELETE' });
          }
        }
        setOrderNotes(orderNotes.filter(note => !selectedItems.includes(note.id)));
        setSelectedItems([]);
        setShowActionDropdown(false);
        await Swal.fire({ icon: 'success', title: 'Deleted!', text: `${selectedItems.length} note(s) deleted successfully!`, timer: 2000, showConfirmButton: false });
      } catch (error) {
        console.error('Error in delete process:', error);
        await Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete notes. Please try again.', confirmButtonColor: '#004aad' });
      }
    }
  };

  const handleClearAll = () => { setSelectedItems([]); setShowActionDropdown(false); };

  const handleAddNew = () => {
    setEditingNote(null);
    setFormData({ orderId: '', note: '', isRequired: false, shopId: selectedShopId, city: '' });
    setIsModalOpen(true);
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setFormData({
      orderId: note.order_Id, // Store the actual integer ID
      note: note.note,
      isRequired: note.isRequired,
      shopId: note.shop_id || selectedShopId,
      city: note.city || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (noteId) => {
    const result = await Swal.fire({
      title: 'Delete Note?',
      text: 'Are you sure you want to delete this note?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const noteToDelete = orderNotes.find(n => n.id === noteId);
        if (noteToDelete && noteToDelete.type === 'order_note_model' && noteToDelete.id) {
          await apiFetch(`/order/api/order-notes/${noteToDelete.id}/?shop_id=${selectedShopId}`, { method: 'DELETE' });
        }
        setOrderNotes(orderNotes.filter(note => note.id !== noteId));
        setSelectedItems(selectedItems.filter(id => id !== noteId));
        await Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Note deleted successfully!', timer: 2000, showConfirmButton: false });
      } catch (error) {
        console.error('Error deleting note:', error);
        await Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete note. Please try again.', confirmButtonColor: '#004aad' });
      }
    }
  };

  const handleSave = async () => {
    // ✅ FIX: formData.orderId now stores the actual order_Id (integer)
    if (!formData.orderId) {
      await Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please select Order ID', confirmButtonColor: '#004aad' });
      return;
    }
    if (!formData.note.trim()) {
      await Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please enter Note', confirmButtonColor: '#004aad' });
      return;
    }

    setIsSaving(true);

    try {
      // ✅ FIX: We already have the actual order_Id, no need to find it from shopOrders
      const actualOrderId = formData.orderId;

      if (editingNote !== null) {
        const updateData = {
          shop_id: parseInt(formData.shopId),
          order_id: actualOrderId,
          note: formData.note,
          is_required: formData.isRequired,
          city: formData.city || ''
        };
        await apiFetch(`/order/api/order-notes/${editingNote.id}/update/`, { method: 'PUT', body: JSON.stringify(updateData) });
        setOrderNotes(orderNotes.map(note => note.id === editingNote.id
          ? { ...note, orderId: formData.orderId, note: formData.note, isRequired: formData.isRequired, city: formData.city }
          : note));
        await Swal.fire({ icon: 'success', title: 'Updated!', text: 'Note updated successfully!', timer: 2000, showConfirmButton: false });
      } else {
        const noteData = {
          shop_id: parseInt(formData.shopId),
          order_id: actualOrderId,
          note: formData.note,
          is_required: formData.isRequired,
          city: formData.city || ''
        };
        const response = await apiFetch('/order/api/order-notes/create/', { method: 'POST', body: JSON.stringify(noteData) });

        // Find the selected order to get its tracking ID for display
        const selectedOrder = shopOrders.find(o => o.order_Id === actualOrderId);

        const newNote = {
          id: response.id || `note_${Date.now()}`,
          orderId: selectedOrder ? selectedOrder.order_Tracking_Id : `ORD-${actualOrderId}`,
          note: formData.note,
          isRequired: formData.isRequired,
          order_Id: actualOrderId,
          shop_id: parseInt(formData.shopId),
          shop_name: shops.find(s => String(s.shop_id) === String(formData.shopId))?.shop_name || 'N/A',
          city: formData.city,
          created_at: new Date().toISOString(),
          type: 'order_note_model'
        };
        setOrderNotes([newNote, ...orderNotes]);
        await Swal.fire({ icon: 'success', title: 'Added!', text: 'Note added successfully!', timer: 2000, showConfirmButton: false });
      }

      setIsModalOpen(false);
      setEditingNote(null);
      setFormData({ orderId: '', note: '', isRequired: false, shopId: selectedShopId, city: '' });
    } catch (err) {
      console.error('Error saving note:', err);
      await Swal.fire({ icon: 'error', title: 'Error Saving Note', text: err.message || 'Failed to save note. Please try again.', confirmButtonColor: '#004aad' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingNote(null);
    setFormData({ orderId: '', note: '', isRequired: false, shopId: selectedShopId, city: '' });
  };

  const handleClickOutside = useCallback((e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
    }
  }, [showActionDropdown]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => { document.removeEventListener('click', handleClickOutside); };
  }, [handleClickOutside]);

  return (
    <div className="ordernotes-main-container">
      <div className="ordernotes-header-section">
        <div className="ordernotes-header-left">
          <GiBorderedShield className="ordernotes-header-icon" />
          <h2 className="ordernotes-header-title">Order Notes</h2>
          <span className="shop-city-badge">
            <FaMapMarkerAlt className="shop-city-icon" />
            <span className="shop-city-name">
              {selectedFilterCity
                ? `${selectedFilterCity} (${cityCounts[selectedFilterCity] || 0})`
                : `All Cities (${filteredNotes.length})`}
            </span>
          </span>
        </div>

        <div className="ordernotes-header-right">
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedFilterCity}
              onChange={handleFilterCityChange}
              disabled={loadingFilterCities || filterCities.length === 0}
            >
              <option value="">
                {loadingFilterCities ? 'Loading...' : 'All Cities'}
              </option>
              {filterCities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({cityCounts[city] || 0})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by Order ID, Note, or City..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>

          <button className="select-all-btn" onClick={handleSelectAll}>
            <FaCheckDouble />
            <span>{selectedItems.length === filteredNotes.length ? 'Deselect All' : 'Select All'}</span>
          </button>

          <button className="add-note-btn" onClick={handleAddNew}>
            <FaPlus />
            <span>ADD ORDER NOTE</span>
          </button>
        </div>
      </div>

      <div className="ordernotes-content">
        {selectedItems.length > 0 && (
          <div className="selected-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">{selectedItems.length} note(s) selected</span>
              <div className="action-dropdown-container">
                <button
                  className="action-dropdown-btn"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                >
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button
                      className="dropdown-item delete-item"
                      onClick={handleDeleteSelected}
                    >
                      <FaTrash />
                      <span>Delete Selected ({selectedItems.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button className="clear-selection" onClick={handleClearAll}>
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="ordernotes-table-container">
          <div className="ordernotes-table-wrapper">
            {error ? (
              <div className="error-state">
                <p>Error: {error}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
              </div>
            ) : (
              <table className="ordernotes-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <input
                        type="checkbox"
                        checked={selectedItems.length === filteredNotes.length && filteredNotes.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Order ID</th>
                    <th>Note</th>
                    <th>City</th>
                    <th>Required</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotes.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="no-data-cell">
                        <FaBuilding className="no-data-icon" />
                        <p>
                          {selectedFilterCity
                            ? `No order notes found in "${selectedFilterCity}".`
                            : `No order notes found for ${currentShopName}.`}
                        </p>
                        <button className="add-first-btn" onClick={handleAddNew}>
                          <FaPlus /> Add Your First Note
                        </button>
                      </td>
                    </tr>
                  ) : (
                    filteredNotes.map((note) => (
                      <tr key={note.id} className={selectedItems.includes(note.id) ? 'selected' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(note.id)}
                            onChange={() => handleSelectItem(note.id)}
                          />
                        </td>
                        <td className="order-cell">{note.orderId}</td>
                        <td className="note-cell">{note.note}</td>
                        <td className="city-cell">
                          {note.city ? note.city : '-'}
                        </td>
                        <td className="required-cell">
                          <span className={`status-badge ${note.isRequired ? 'active' : 'inactive'}`}>
                            {note.isRequired ? 'Required' : 'Not Required'}
                          </span>
                        </td>
                        <td className="actions-cell">
                          <div className="actions-cell-inner">
                            <button
                              className="edit-note-btn"
                              onClick={() => handleEdit(note)}
                              title="Edit Note"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="delete-note-btn"
                              onClick={() => handleDelete(note.id)}
                              title="Delete Note"
                            >
                              <RiDeleteBinFill />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingNote !== null ? 'Edit Order Note' : 'Add Order Note'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <form className="note-form" onSubmit={(e) => e.preventDefault()}>
                <div className="form-group">
                  <label>Shop *</label>
                  <select
                    name="shopId"
                    value={formData.shopId}
                    onChange={(e) => {
                      const newShopId = e.target.value;
                      setFormData({...formData, shopId: newShopId, orderId: '', city: ''});
                      setSelectedShopId(newShopId);
                      localStorage.setItem('selectedShopId', newShopId);
                      navigate(`/shop/${newShopId}/Ordernotes`);
                    }}
                    className="form-select"
                    required
                    disabled={editingNote !== null}
                  >
                    {shops.map(shop => (
                      <option key={shop.shop_id} value={shop.shop_id}>
                        {shop.shop_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <select
                      name="city"
                      value={formData.city || ''}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      className="form-select"
                      disabled={!formData.shopId || loadingCities}
                    >
                      <option value="">-- Select a city --</option>
                      {loadingCities ? (
                        <option value="" disabled>Loading cities...</option>
                      ) : cities.length > 0 ? (
                        cities.map((city, index) => (
                          <option key={index} value={city}>{city}</option>
                        ))
                      ) : (
                        <option value="" disabled>No cities available</option>
                      )}
                    </select>
                    {cityFetchError && <p className="field-hint error-hint">{cityFetchError}</p>}
                  </div>

                  <div className="form-group">
                    <label>Order ID *</label>
                    <select
                      name="orderId"
                      value={formData.orderId}
                      onChange={(e) => setFormData({...formData, orderId: e.target.value})}
                      className="form-select"
                      required
                    >
                      <option value="">Select Order</option>
                      {shopOrders.length > 0 ? (
                        shopOrders.map(order => (
                          /* ✅ FIX: Value is now the unique order_Id, Display shows Tracking ID + City */
                          <option key={order.order_Id} value={order.order_Id}>
                            {order.order_Tracking_Id} ({order.city || 'No City'})
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No orders found for selected shop</option>
                      )}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Note *</label>
                  <textarea
                    name="note"
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    placeholder="Enter note description"
                    rows="4"
                    required
                  />
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isRequired}
                      onChange={(e) => setFormData({...formData, isRequired: e.target.checked})}
                    />
                    <span>Required Note</span>
                  </label>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCloseModal} disabled={isSaving}>Cancel</button>
              <button className="btn-save" onClick={handleSave} disabled={isSaving}>
                {editingNote !== null ? (isSaving ? 'Updating...' : 'Update Note') : (isSaving ? 'Saving...' : 'Save Note')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ordernotes;